import { z } from 'zod';
import { IsoDateTime, PackageId, Uuid } from './primitives.js';
export const PackageInclusions = z.object({
    aiPages: z.number().int().positive(),
    retries: z.number().int().nonnegative(),
    scaffold: z.literal(true),
    contentPack: z.literal(true),
});
export const PackageOption = z.object({
    packageId: PackageId,
    name: z.string(),
    listPriceCents: z.number().int().positive(),
    currency: z.literal('usd'),
    includes: PackageInclusions,
    eligible: z.boolean(),
    /** Why the option is not eligible; null when it is. */
    reason: z.string().nullable(),
});
export const RecommendationSignals = z.object({
    pageCount: z.number().int().nonnegative(),
    collectionCount: z.number().int().nonnegative(),
});
export const Recommendation = z.union([PackageId, z.literal('contact')]);
export const RecommendResponse = z.object({
    signals: RecommendationSignals,
    recommended: Recommendation,
    options: z.array(PackageOption),
});
export const CheckoutRequest = z.object({
    packageId: PackageId,
    projectId: Uuid,
});
export const CheckoutResponse = z.object({
    checkoutUrl: z.string().url(),
});
export const EntitlementStatus = z.enum(['active', 'exhausted', 'expired', 'refunded']);
export const EntitlementView = z.object({
    packageId: PackageId,
    projectId: Uuid,
    aiPagesRemaining: z.number().int().min(0),
    retriesRemaining: z.number().int().min(0),
    status: EntitlementStatus,
    expiresAt: IsoDateTime.nullable(),
});
/**
 * Packages bought for one project, newest first. Purchases stack, so this is a list rather than
 * one row — and it is deliberately shaped as remaining AI pages, never as a credit balance
 * (packages-catalog.md §"Copy rules").
 */
export const EntitlementListResponse = z.object({
    items: z.array(EntitlementView),
    /** Sum across active entitlements: what the UI may promise the customer right now. */
    aiPagesRemaining: z.number().int().min(0),
    retriesRemaining: z.number().int().min(0),
});
/** What a payment webhook answers. The provider only needs to know we took it. */
export const WebhookAck = z.object({
    received: z.literal(true),
    /** `ignored` covers events Detachly has no use for, and payments it did not start. */
    effect: z.enum(['entitlement_created', 'entitlement_refunded', 'replayed', 'ignored']),
});
/**
 * Carried through the payment provider so the webhook can create the entitlement
 * without trusting anything else in the payload.
 */
export const PolarCheckoutMetadata = z.object({
    userId: z.string().min(1),
    projectId: Uuid,
    packageId: PackageId,
});
//# sourceMappingURL=billing.js.map